import React, { useState, useEffect, useCallback, useRef } from "react";

const LogoBadge = ({ logo }) => (
  <div className="bg-gray-900/20 p-3 sm:p-4 rounded-lg inline-flex items-start">
    <img
      src={logo}
      alt="logo"
      className="w-14 sm:w-16 md:w-20 lg:w-24 xl:w-24 self-start relative"
    />
    <div className="text-left ml-2 sm:ml-3">
      <h2
        translate="no"
        lang="en"
        className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl font-bold leading-tight"
      >
        INDUSTRIAL
      </h2>
      <h2
        translate="no"
        lang="en"
        className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl font-bold leading-tight"
      >
        BUSINESS
      </h2>
      <h2
        translate="no"
        lang="en"
        className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl font-bold leading-tight"
      >
        CLUSTERS
      </h2>
    </div>
  </div>
);

const Carousel = ({ slides, autoPlayDelay = 6000 }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const touchStartRef = useRef(0);
  const touchEndRef = useRef(0);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      nextSlide();
    }, autoPlayDelay);
    return () => clearInterval(interval);
  }, [isAutoPlaying, nextSlide, autoPlayDelay]);

  const handleTouchStart = (e) => {
    touchStartRef.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndRef.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartRef.current || !touchEndRef.current) return;
    const distance = touchStartRef.current - touchEndRef.current;
    if (distance > 50) {
      nextSlide();
    } else if (distance < -50) {
      prevSlide();
    }
    touchStartRef.current = 0;
    touchEndRef.current = 0;
  };

  // Solo renderizar el slide actual y los adyacentes
  const isSlideVisible = (index) => {
    if (index === currentSlide) return true;
    const prev = (currentSlide - 1 + slides.length) % slides.length;
    const next = (currentSlide + 1) % slides.length;
    return index === prev || index === next;
  };

  return (
    <div
      className="relative overflow-hidden h-[450px] sm:h-[520px] md:h-[600px] lg:h-[700px]"
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {slides.map((slide, index) => {
        const isActive = index === currentSlide;
        const shouldRender = isSlideVisible(index);

        if (!shouldRender) return null;

        return (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-500 ease-in-out ${
              isActive ? "opacity-100 z-10" : "opacity-0 z-0"
            }`}
            style={{ willChange: isActive ? "opacity" : "auto" }}
          >
            <div className="absolute inset-0">
              <img
                src={slide.image}
                alt={slide.alt || `Slide ${index + 1}`}
                className="w-full h-full object-cover object-center"
                loading={index === 0 ? "eager" : "lazy"}
                decoding="async"
              />
            </div>
            <div className="absolute inset-0 bg-gray-900/40"></div>
            <div className="absolute inset-0 flex flex-col justify-start p-6 sm:p-8 md:p-10 lg:p-14 text-white">
              {slide.showLogo && slide.logoPosition === "top-left" && (
                <div className="flex flex-row items-start gap-4 sm:gap-6 md:gap-8 pl-0 sm:pl-4 md:pl-8">
                  <LogoBadge logo={slide.logo} />
                </div>
              )}
              {slide.showLogo && slide.logoPosition === "top-right" && (
                <div className="flex flex-row items-start justify-end gap-4 sm:gap-6 md:gap-8 pr-0 sm:pr-4 md:pr-8">
                  <LogoBadge logo={slide.logo} />
                </div>
              )}
              {(slide.logoPosition === "top-left" ||
                slide.logoPosition === "top-right") && (
                <div className="flex flex-col justify-start h-auto mt-3 sm:mt-4 md:mt-5 lg:mt-6 max-w-full">
                  <div className="text-left">
                    <h2 className="text-lg sm:text-xl md:text-2xl lg:text-4xl xl:text-5xl font-bold leading-tight">
                      {slide.title}
                    </h2>
                    {slide.subtitle && (
                      <p className="inline-block bg-blue-600/5 backdrop-blur-sm px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl font-medium mt-3 sm:mt-4">
                        {slide.subtitle}
                      </p>
                    )}
                  </div>
                </div>
              )}
              {slide.logoPosition === "left" && (
                <div className="flex flex-row items-start gap-4 sm:gap-6 md:gap-8 h-auto mt-0 sm:mt-1 md:mt-2 lg:mt-3 max-w-full">
                  {slide.showLogo && (
                    <div className="flex-shrink-0">
                      <LogoBadge logo={slide.logo} />
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="text-left">
                      <h2 className="text-lg sm:text-xl md:text-2xl lg:text-4xl xl:text-5xl font-bold leading-tight">
                        {slide.title}
                      </h2>
                      {slide.subtitle && (
                        <p className="inline-block bg-blue-600/5 backdrop-blur-sm px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl font-medium mt-3 sm:mt-4">
                          {slide.subtitle}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {slide.logoPosition === "bottom-center" && (
                <div className="flex flex-col justify-center items-center gap-4 h-auto mt-4 sm:mt-6 md:mt-8 lg:mt-12 max-w-full">
                  <div className="text-center">
                    <h2 className="text-lg sm:text-xl md:text-2xl lg:text-4xl xl:text-5xl font-bold leading-tight">
                      {slide.title}
                    </h2>
                    {slide.subtitle && (
                      <p className="inline-block bg-blue-600/5 backdrop-blur-sm px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl font-medium mt-3 sm:mt-4">
                        {slide.subtitle}
                      </p>
                    )}
                  </div>
                  {slide.showLogo && <LogoBadge logo={slide.logo} />}
                </div>
              )}
            </div>
          </div>
        );
      })}
      <button
        onClick={prevSlide}
        className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-2 sm:p-3 rounded-full transition-all duration-300 group"
        aria-label="Anterior"
      >
        <svg
          className="w-5 h-5 sm:w-6 sm:h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 19l-7-7 7-7"
          />
        </svg>
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-2 sm:p-3 rounded-full transition-all duration-300 group"
        aria-label="Siguiente"
      >
        <svg
          className="w-5 h-5 sm:w-6 sm:h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5l7 7-7 7"
          />
        </svg>
      </button>
      {/* Botón CTA flotante encima de los dots */}
      {slides[currentSlide]?.cta && (
        <div className="absolute bottom-14 sm:bottom-16 left-1/2 -translate-x-1/2 z-20">
          <a
            href={slides[currentSlide]?.ctaLink || "/contact"}
            className="inline-block bg-[#0092ff] hover:bg-[#1e72b9] text-white font-semibold px-5 py-2.5 sm:px-6 sm:py-3 rounded-lg text-sm sm:text-base md:text-lg transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
          >
            {slides[currentSlide]?.cta}
          </a>
        </div>
      )}
      <div className="absolute bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2 sm:gap-3">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`transition-all duration-300 rounded-full ${
              index === currentSlide
                ? "w-8 sm:w-10 h-2 sm:h-2.5 bg-[#0092ff]"
                : "w-2 sm:w-2.5 h-2 sm:h-2.5 bg-white/50 hover:bg-white/70"
            }`}
            aria-label={`Ir al slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default Carousel;
